import i18next from '../../../../../i18n';

import type { CampaignSource, ICashbackLevel, IRebateCampaign, IUserProgress } from 'features/campaigns';
import { REBATE_CAMPAIGN_TYPE, REBATE_STATUS_ORDER } from 'features/campaigns';
import { evaluateCampaignStatus } from 'features/campaigns/hooks';
import { findTranslationKeyAsGivenJSONTranslationFallback, formatCurrencyValueWithSpaces } from 'shared/utils/helpers';
import { LEVEL_ORDER } from '../campaignHelpers';

export const REBATE_CARD_INFO_PLACEHOLDER = '---';

export interface RebateCardData {
  /** Display-ready next win amount, e.g. "10" or "10.50", or '---' when not shown (see `getNextWinDisplay`). */
  nextWinDisplay: string;
  /** Display-ready level progress, e.g. "Level 1/3", or '---' when not shown. */
  progressDisplay: string;
  /** Whether the next-win value is a real amount (not '---') and should show currency. */
  showCurrency: boolean;
}

export const isRebateSource = (source: CampaignSource): source is IRebateCampaign => {
  return 'type' in source && source.type === REBATE_CAMPAIGN_TYPE;
};

/**
 * A cashback level is unlocked once the player has joined (spend is known,
 * even if 0) and the spend reached the level's floor.
 */
export const isLevelUnlocked = (spent: number | null | undefined, level: ICashbackLevel): boolean =>
  spent !== null && spent !== undefined && spent >= level.betRangeFrom;

/**
 * A cashback level is finished once spend has reached its `betRangeTo`
 * (inclusive) — it is no longer the level being progressed through.
 */
export const isLevelFinished = (spent: number | null | undefined, level: ICashbackLevel): boolean =>
  !!spent && spent >= level.betRangeTo;

/**
 * The cashback level the player's spend currently falls into (the highest level
 * whose `betRangeFrom` has been reached), or `undefined` if none apply.
 */
// fallow-ignore-next-line unused-export
export const findCurrentLevel = (levels: ICashbackLevel[], spent: number | null): ICashbackLevel | undefined =>
  spent === null
    ? undefined
    : [...levels].sort((a, b) => b.betRangeFrom - a.betRangeFrom).find((l) => spent >= l.betRangeFrom);

export const hasSpentBetReachedMaxAmount = (rebate: IRebateCampaign): boolean => {
  const levels = rebate.cashbackLevels;
  const progress = rebate.userProgress;
  if (levels.length === 0 || !progress) return false;

  const highestLevel = levels.reduce((max, l) => (l.betRangeTo > max.betRangeTo ? l : max));
  return progress.spentBetAmount >= highestLevel.betRangeTo;
};

/**
 * The `maxPayoutAmount` the player can win next: level 1's when not joined or
 * below level 1's floor, the current level's while it isn't finished, the
 * next level's (by `betRangeFrom`) once the current one is finished, and
 * `null` once spend has reached the top level's ceiling (or there are no levels).
 */
export const getNextWinAmount = (rebate: IRebateCampaign): number | null => {
  const levels = rebate.cashbackLevels;
  if (levels.length === 0) return null;

  const firstLevel = levels.reduce((min, l) => (l.betRangeFrom < min.betRangeFrom ? l : min));
  const progress = rebate.userProgress;

  if (!rebate.joined || !progress || progress.spentBetAmount < firstLevel.betRangeFrom) return firstLevel.maxPayoutAmount;
  if (hasSpentBetReachedMaxAmount(rebate)) return null;

  const currentLevel = findCurrentLevel(levels, progress.spentBetAmount) ?? firstLevel;
  if (!isLevelFinished(progress.spentBetAmount, currentLevel)) return currentLevel.maxPayoutAmount;

  const nextLevel = [...levels]
    .sort((a, b) => a.betRangeFrom - b.betRangeFrom)
    .find((l) => l.betRangeFrom > currentLevel.betRangeFrom);
  return nextLevel ? nextLevel.maxPayoutAmount : null;
};

/**
 * Whether the player's spend currently falls within the given level's own
 * bet range (i.e. this is the level they're actively progressing through).
 */
export const isCurrentLevel = (spent: number | null | undefined, level: ICashbackLevel): boolean => {
  return !!spent && spent >= level.betRangeFrom && spent <= level.betRangeTo;
};

/**
 * A cashback level's fill percentage (0..100): 100 once spend reached the
 * level's ceiling, the server-provided `levelProgress` while it's the active
 * level, and 0 otherwise.
 */
export const getLevelFillPercent = (userProgress: IUserProgress | null, level: ICashbackLevel): number => {
  if (!userProgress) return 0; // user hasn't joined the campaign

  const spent = userProgress.spentBetAmount;

  if (isLevelFinished(spent, level)) return 100; // Level finished (spend reached its ceiling) — segment full.
  if (isCurrentLevel(spent, level)) return userProgress.levelProgress; // Active segment — server-computed level progress (already 0..100).
  return 0;
};

/**
 * Display-ready "Next Win" value shared by the rebate card and the Winnings tab:
 * '---' while the campaign is in promo or finished, or when there is no next win
 * amount; otherwise `getNextWinAmount` passed through `format` (2 decimals by default).
 */
export const getNextWinDisplay = (
  rebate: IRebateCampaign,
  status: { isPromo: boolean; isFinished: boolean },
  format: (amount: number) => string = (amount) => formatCurrencyValueWithSpaces(amount, true)
): string => {
  if (status.isPromo || status.isFinished) return REBATE_CARD_INFO_PLACEHOLDER;
  const nextWinAmount = getNextWinAmount(rebate);
  return nextWinAmount ? format(nextWinAmount) : REBATE_CARD_INFO_PLACEHOLDER;
};

export const buildRebateCardInfo = (rebate: IRebateCampaign): RebateCardData => {
  const progress = rebate.userProgress;
  const status = evaluateCampaignStatus(rebate);
  const nextWinDisplay = getNextWinDisplay(rebate, status, formatCurrencyValueWithSpaces);
  const showCurrency = nextWinDisplay !== REBATE_CARD_INFO_PLACEHOLDER;

  // Not joined or still in promo — no level to show yet.
  if (!rebate.joined || !progress || status.isPromo) {
    return {
      nextWinDisplay,
      progressDisplay: REBATE_CARD_INFO_PLACEHOLDER,
      showCurrency,
    };
  }

  const spentBetAmount = progress.spentBetAmount ?? 0;
  const levels = rebate.cashbackLevels ?? [];

  let progressDisplay = REBATE_CARD_INFO_PLACEHOLDER;
  const currentLevel = findCurrentLevel(levels, spentBetAmount);
  if (currentLevel) {
    progressDisplay = i18next.t('rebate.level', {
      number: `${currentLevel.levelNumber}/${levels.length}`,
      defaultValue: findTranslationKeyAsGivenJSONTranslationFallback('rebate.level') ?? '',
    });
  }

  return { nextWinDisplay, progressDisplay, showCurrency };
};

export const sortRebateCampaigns = (campaigns: IRebateCampaign[]): IRebateCampaign[] => {
  return [...campaigns].sort((a, b) => {
    // 1. Status: Active > Promo > Completed
    const statusDiff = REBATE_STATUS_ORDER[a.status] - REBATE_STATUS_ORDER[b.status];
    if (statusDiff !== 0) return statusDiff;

    // 2. Joined first
    if (a.joined !== b.joined) return a.joined ? -1 : 1;

    // 3. Priority (level): Gold > Purple > Red > Blue > Green > Madness
    const levelDiff = LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level];
    if (levelDiff !== 0) return levelDiff;

    // 4. End time ascending (closest end time first)
    return new Date(a.endDate).getTime() - new Date(b.endDate).getTime();
  });
};
